<template lang="pug">
  #product-review-summary
    template(v-if='isEnhancedMwebReviewPage && isMobile && requestFromReviewPage()')
      .review-rating-summary
        .star-icon
        .rating-text
          span.average-rating {{ averageRating }}
          span  ({{ totalReview }})

    template(v-else)
      .row.title
        i18n.col-md-1.title-text(path='account.reviews.title')
        .tooltip
          .mask(@mouseenter='toggleTooltipVisibility' @mouseleave='toggleTooltipVisibility')
            include ../../assets/images/account/orders/info.svg
          .review-infotip-web-hover(v-show='tooltipVisible')
            .svg-icon.close2(@click='toggleTooltipVisibility' v-if='isMobile')
            .text(v-t='"products.reviews.starDescription"')
            .text(v-t='"common.example"')
            .stars
              .text.row(v-for='review in reviewExplainations()' :key="review['rating']")
                .col-md-1.number-of-star {{ review['rating'] }}
                .star-icon
                  .rating-container
                    .rateit
                      .rateit-preset
                .col.star-text = {{ review['description'] }}

      .row.summary
        a.col-md-4.col.rating-overview(href='#' @click='viewAllReviews')
          .row.averate-rating {{ averageRating }}
          .row.star-bar.rating-container.rating-stars
            ProductRatingStars(:rating='averageRating')
          .row.total-review {{ totalReview }} {{ this.$t('products.reviews.title') }}
        .col-md-8.rating-details
          .row.rating-bar(v-for='(rate, key) in ratings' :key="rate['rating-value']")
            .col-md-3.bar-title.row
              .col.star-text {{ rate['rating-value'] }}
              .col.star-icon
                .rating-container
                  .rateit
                    .rateit-preset
            a.col-md-7.row.bar-body(
              v-if='isEnhancedMwebReviewPage',
              v-bind:class="{ 'bar-active': selectedRatings.includes(rate['rating-value']) }",
              @click="triggerFilterByRatings($event, rate['rating-value'])"
            )
              .col-md-8.bar-holder
                .bar-content(v-bind:style="barProgression(rate)") &nbsp;
              .col-md-1.bar-value {{ rate.count }}
            a.col-md-7.row.bar-body(
              v-else href='#reviews'
              v-bind:class="{ 'bar-active': filterByRating === rate['rating-value'] }"
              @click="triggerFilterByRating($event, rate['rating-value'])"
            )
              .col-md-8.bar-holder
                .bar-content(v-bind:style="barProgression(rate)") &nbsp;
              .col-md-1.bar-value {{ rate.count }}
      router-link.btn.write-review-btn(:to='{ path: "/account/reviews" }' v-t='"products.reviews.write"' v-if='!isMobile && !bvHostedUiEnabled')
</template>

<script>
import { mapState } from 'vuex'
import { reviewsMixin } from '@/mixins/reviews-mixin'
import ProductRatingStars from './product-rating-stars.vue'
import { pushDataLayerEvent } from '@/modules/services/gtm'

export default {
  components: {
    ProductRatingStars
  },

  mixins: [reviewsMixin],

  props: {
    reviewMeta: {
      type: Object,
      default: () => {}
    },
    targetContainer: {
      type: String,
      default: 'pdp'
    }
  },

  data () {
    return {
      tooltipVisible: false,
      barProgression (rate) {
        const style = { width: `${rate.percent}%` }
        if (rate.percent > 0) {
          // this is due to round visual effect, otherwise it will look flat and distorted
          style['min-width'] = '16px'
        }

        return style
      },
      isEnhancedMwebReviewPage: false
    }
  },

  computed: {
    ...mapState({
      filterByRating: state => state.review.filterByRating,
      selectedRatings: state => state.review.selectedRatings,
      product: state => state.product.product
    }),
    ratings () {
      let ratingSummaries = this.reviewMeta['rating-distribution']
      const totalReview = this.totalReview
      ratingSummaries = ratingSummaries.map((rate) => ({
        ...rate,
        percent: totalReview === 0 ? 0 : parseInt(rate.count / totalReview * 100, 10)
      }))
      return ratingSummaries.sort(
        (r1, r2) => r2['rating-value'] - r1['rating-value']
      )
    },
    isMobile: () => vueSupport.isMobile,

    averageRating () {
      return this.reviewMeta['average-rating']
    },

    totalReview () {
      return this.reviewMeta['total-ratings']
    },
    bvHostedUiEnabled: () => vueSupport?.featureFlags?.enableBvHostedUi
  },

  mounted () {
    this.isEnhancedMwebReviewPage = this.$kameleoon.isFeatureFlagActive('enable_enhanced_mweb_review_page')
  },

  methods: {
    viewAllReviews (e) {
      e.preventDefault()
      this.$store.commit('resetReviewsFilters')
      this.fetchReviews()
    },

    triggerFilterByRating (e, rating) {
      this.$store.commit('setReviewsFilterByRating', rating)
      this.fetchReviews()

      const reviewSummary = document.getElementById('product-review-summary')
      if (reviewSummary) {
        reviewSummary.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    triggerFilterByRatings (e, rating) {
      if (this.selectedRatings.includes(rating)) {
        this.$store.commit('setSelectedRatings', this.selectedRatings.filter(item => item !== rating))
      } else {
        this.$store.commit('setSelectedRatings', [...this.selectedRatings, rating])
      }
      this.$store.commit('setReviewsFilterByRating', this.selectedRatings.join(','))
      this.recordRatingFilterGtmEvent(rating)
      this.fetchReviews()
    },

    reviewExplainations () {
      return [
        { rating: 5, description: '5 + 4.5 star reviews' },
        { rating: 4, description: '4 + 3.5 star reviews' },
        { rating: 1, description: '1 + 0.5 star reviews' }
      ]
    },

    toggleTooltipVisibility () {
      this.tooltipVisible = !this.tooltipVisible
    },

    requestFromPdp () {
      return this.targetContainer === 'pdp'
    },

    requestFromReviewPage () {
      return this.targetContainer === 'review-page'
    },

    recordRatingFilterGtmEvent (rating) {
      const filterType = this.selectedRatings.includes(rating) ? 'rating_selected' : 'rating_deselected'

      pushDataLayerEvent(
        'click_filter_reviews', {
          params: {
            product_id: this.product.id,
            filter_type: filterType,
            filter_value: rating
          }
        }
      )
    }
  }
}
</script>

<style lang="scss">
#product-review-summary {
  .rating-stars {
    @include media-breakpoint-up(md) {
      .rateit-range,
      .rateit-selected,
      .rateit-preset {
        background-size: 15px 12px !important;
      }
    }
  }
}
</style>

<style scoped lang="scss">
#product-review-summary {
  .review-rating-summary {
    display: flex;
    align-items: center;
    padding: 0px 2px;

    .star-icon {
      background: url('../../../../app/assets/images/sephora-stars.png') 0px -15px;
      height: 14px;
      width: 14px;
      background-repeat: repeat-x;
    }

    .rating-text {
      display: flex;
      margin-left: 3px;
      gap: 5px;

      .average-rating {
        font-weight: bold;
      }
    }
  }

  margin: 2em;

  @include media-breakpoint-down(sm) {
    margin: 10px 0;
  }

  a.bar-value {
    text-decoration: revert;
  }

  .title {
    text-transform: uppercase;
    color: $black;
    @include font-sephora-sans;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 0.3px;
    line-height: 19px;
    border-bottom: 1px solid $grey;
    padding: 1em 0;

    @include media-breakpoint-down(sm) {
      padding: 1em;
      border-bottom: 0;
    }

    .title-text {
      padding-top: 3px;
      padding-left: 0;
      padding-right: 20px;
      width: auto;
    }

    .mask {
      height: 20px;
      width: 20px;
      svg path {
        fill: $grey;
      }
    }
  }

  .i-icon {
    border-radius: 50%;
    background-color: $white;
    width: 20px;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .summary {
    width: 80%;
    margin: auto;
    padding: 3em 0;
    justify-content: center;

    @include media-breakpoint-down(sm) {
      flex-wrap: nowrap;
      padding: 0.5em 0;
      width: 320px;
    }

    .rating-overview {
      max-width: 125px;
      min-width: 125px;
      margin: auto;
      margin-right: revert;
      padding-right: 0px;

      @media (max-width: 768px) {
        min-width: 110px;
      }

      @include media-breakpoint-down(sm) {
        max-width: 100px;
        min-width: 100px;
        padding: 0;
      }

      .averate-rating {
        color: $black;
        @include font-sephora-sans;
        font-size: 45px;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 54px;
        justify-content: center;
      }

      .star-bar {
        justify-content: center;
      }

      .total-review {
        padding-top: 1em;
        color: $black;
        @include font-sephora-sans;
        font-size: 14px;
        font-weight: 300;
        letter-spacing: 0.3px;
        line-height: 17px;
        margin: 0 auto;
        justify-content: center;
        text-decoration-line: underline;
      }
    }

    .rating-details {
      min-width: 450px;
      padding-left: 0px;

      @media (max-width: 768px) {
        min-width: 400px;
      }

      @include media-breakpoint-down(sm) {
        min-width: 250px;
        max-width: 250px;
        padding: 0 0;
      }
      /* INDIVIDUAL BAR HOLDER */

      .rating-bar {
        padding: 0.5em 0;

        @include media-breakpoint-down(sm) {
          flex-wrap: nowrap;
          padding: 0.25em 0;
          margin-left: 0;
          margin-right: 0;
          width: auto;

          .bar-body {
            padding-left: 15px;
            width: auto;
          }
        }

        .bar-body {
          display: contents;
          cursor: pointer;
          .bar-holder {
            width: 221px;
            height: 15px;
            border-radius: 8px;
            -webkit-border-radius: 8px;
            -moz-border-radius: 8px;
            -ms-border-radius: 8px;
            -o-border-radius: 8px;
            box-sizing: border-box;
            padding: 0;
            overflow: hidden;
            box-shadow: 0 0 0 1px $grey inset;

              @include media-breakpoint-down(sm) {
                width: 125px;
                height: 11px;
                margin-left: 10px;
              }
            }
        }

        .bar-value {
          text-decoration-line: underline;
          @include media-breakpoint-down(sm) {
            width: auto;
          }
        }

        .bar-content {
          height: 100%;
          background-color: #999999;
          color: $white;
          border-radius: 100px;
          -webkit-border-radius: 100px;
          -moz-border-radius: 100px;
          -ms-border-radius: 100px;
          -o-border-radius: 100px;
          text-align: center;
          font-weight: 400;
        }

        .bar-active {
          .bar-value {
            color: $brand-primary;
          }

          .bar-content {
            background-color: $brand-primary;
          }
        }

        .bar-title {
          padding-right: 0;
          margin-right: inherit;

          @include media-breakpoint-down(sm) {
            width: 20%;
            margin-right: 0;
          }

          .star-icon {
            padding-right: 0;

            @include media-breakpoint-down(sm) {
              padding-left: 5px;
            }

            .rating-container {
              display: flex;

              .rateit .rateit-preset {
                background-size: 15px 12px;
                height: 12px;
                width: 15px;
              }
            }
          }

          .star-text {
            margin-right: inherit;
            font-weight: bold;
            text-align: end;
            padding-right: 0.5em;

            @include media-breakpoint-down(sm) {
              padding-right: 0.15em;
            }
          }
        }
      }
    }
  }

  .write-review-btn {
    margin: auto;
    display: block;
    box-sizing: border-box;
    height: 40px;
    width: 161px;
    border: 1px solid $black;
    background-color: $white;
    color: $black;
    @include font-sephora-sans;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0.3px;
    line-height: 17px;
    text-align: center;
  }

  .tooltip {
    position: relative;
    display: inline-block;
    margin-left: inherit;
  }

  .tooltip .review-infotip-web-hover {
    box-sizing: border-box;
    width: 224px;
    border: 1px solid #999999;
    background-color: $white;
    text-transform: none;

    /* Position the tooltip */
    position: absolute;
    z-index: 1;
    padding: 1em;

    @include media-breakpoint-down(sm) {
      width: 250px;
      margin: 10px -50px;
      padding-top: 0;
    }

    .close2 {
      margin-left: 90%;
      height: 30px;
      width: 30px;
      vertical-align: middle;
    }

    .text {
      width: 192px;
      padding: 3px 0;
      color: $black;
      @include font-sephora-sans;
      font-size: 12px;
      font-weight: 400;
      letter-spacing: 0.3px;
      line-height: 15px;

      @include media-breakpoint-down(sm) {
        width: auto;
      }
    }

    .stars {
      .number-of-star {
        margin-top: -2px;
        margin-right: -5px;
        font-weight: bold;
        padding-top: 2px;

        @include media-breakpoint-down(sm) {
          width: auto;
        }
      }

      .star-icon {
        padding-right: 0;

        @include media-breakpoint-down(sm) {
          margin-right: 5px;
          width: auto;
        }

        .rating-container {
          display: flex;

          .rateit .rateit-preset {
            background-size: 15px 12px;
            height: 12px;
            width: 15px;
          }
        }
      }
    }
  }
}
</style>
